import { usePrevious } from '@ps/web-shared-components';
import classnames from 'clsx';
import React, { useEffect, useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import { ModalRoutes } from '../../../../types/common';
import ArrowDown from '../assets/ArrowDown.svg';
import ArrowUp from '../assets/ArrowUp.svg';
import Bin from '../assets/Bin.svg';
import styles from '../shared.scss';
import getDateDetails from '../utils/getDateDetails';
import type { NormalizedWallPosts } from '../utils/normalizeWallPosts';

import Comment from './Comment';
import Reply from './Reply';
import { TXTCLI_Hide_Replies, TXTCLI_Show_Replies, TXTWCL_Reply } from './WallPost.messages.json';

export interface ReplyProps {
  post: string;
  parentId: number;
}

interface Props {
  post: NormalizedWallPosts;
  loading: boolean;
  handleReply?: ({ post, parentId }: ReplyProps) => void;
  handleDeleteWallPost?: (id: number) => void;
  canManage: boolean;
  isRightSide?: boolean;
}

const Wall: React.FC<Props> = (props) => {
  const [showComment, setShowComment] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const { formatMessage } = useIntl();
  const { modal } = useDispatch<Dispatch>();

  const userId = useSelector(select.account.getUserId);
  const { post, loading, handleReply, handleDeleteWallPost, canManage, isRightSide } = props;

  const prevPost = usePrevious(post);

  const isOwnPost = userId === post.userId;
  const { isSameYear, isSameDay, isSameMonth, isYesterday } = getDateDetails(post.created);
  const hideDayAndMonth = (isSameYear && isSameMonth && isSameDay) || isYesterday;
  const replyPlaceholder = formatMessage(TXTWCL_Reply);

  const canDelete = canManage || isOwnPost;

  useEffect(() => {
    if (
      post.comments &&
      prevPost.comments &&
      post.comments.length === 1 &&
      prevPost.comments.length === 0
    ) {
      setShowComment(true);
    }
  }, [post, prevPost]);

  const handleSubmit = (message: string) =>
    handleReply?.({
      post: message,
      parentId: post.id || 0
    });

  const handleDelete = () => {
    if (post.id) handleDeleteWallPost?.(post.id);
  };

  const handleDeleteThread = () => {
    modal.enqueue({
      path: ModalRoutes.deleteThreadPrompt,
      locationState: { thread: post, handleDelete, loading }
    });
  };

  const showDelete = canDelete && !loading;

  return (
    <div className={classnames(styles.wallPostContainer, { [styles.rightSide]: isRightSide })}>
      <div
        key={post.id}
        className={classnames(styles.wallPost, { [styles.expanded]: showComment || showReply })}
      >
        <div className={styles.info}>
          <span className={isOwnPost ? styles.usernameOwn : styles.username}>{post.userId}</span>
          <span className={styles.date}>
            {isYesterday && <span>Yesterday, </span>}
            <FormattedDate
              value={post.created}
              day={hideDayAndMonth ? undefined : '2-digit'}
              month={hideDayAndMonth ? undefined : 'short'}
              year={isSameYear ? undefined : 'numeric'}
              hour="2-digit"
              minute="numeric"
              hour12={false}
            />
          </span>
        </div>
        <p className={styles.content}>{post.content}</p>
        {post.comments.length > 0 && !isRightSide && (
          <>
            {!showComment && (
              <StarsButton
                className={styles.commentCtrl}
                variant="text"
                block
                icon={<ArrowDown className={styles.icon} />}
                iconPosition="after"
                type="submit"
                aria-label="Show Replies"
                translation={TXTCLI_Show_Replies}
                onClick={() => setShowComment(true)}
              />
            )}
            {showComment && (
              <StarsButton
                className={styles.commentCtrl}
                variant="text"
                icon={<ArrowUp className={styles.icon} />}
                iconPosition="after"
                block
                type="submit"
                aria-label="Show Replies"
                translation={TXTCLI_Hide_Replies}
                onClick={() => setShowComment(false)}
              />
            )}
            {showComment && (
              <div className={styles.comments}>
                {post.comments.map((comment) => (
                  <Comment
                    key={comment.id}
                    comment={comment}
                    userId={userId}
                    handleDeleteWallPost={handleDeleteWallPost}
                    canManage={canManage}
                    loading={loading}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {post.comments.length === 0 && !isRightSide && (
          <StarsButton
            className={styles.reply}
            variant="text"
            block
            type="submit"
            translation={TXTWCL_Reply}
            onClick={() => setShowReply(!showReply)}
          />
        )}
        {(showComment || showReply) && !isRightSide && (
          <Reply
            loading={loading}
            placeholder={replyPlaceholder}
            handleSubmit={handleSubmit}
            containerClassName={styles.replyComment}
            replyFieldClassName={styles.replyFieldPost}
            textClassName={classnames(styles.replyMessage, styles.replyMessagePost)}
            buttonClassName={styles.replyBtn}
          />
        )}
      </div>
      {showDelete && !isRightSide && (
        <StarsButton
          className={styles.deleteThread}
          onClick={handleDeleteThread}
          block
          icon={<Bin />}
          variant="text"
          disabled={loading}
          type="submit"
        />
      )}
    </div>
  );
};

export default Wall;
